import { TESTS_CONFIG } from "../../MT_Score/data.js";

/**
 * Creates standardized FormData payload for MockTest questions
 * @param {Object} params - Parameters for creating the payload
 * @param {Object} params.quest - Question object containing question metadata
 * @param {Object} params.questData - Additional question data from API
 * @param {string|number} params.userResponse - User's response (transcript, text, answers, etc.)
 * @param {string|number} params.marksObtained - Score obtained by user
 * @param {number} params.timeTaken - Time taken in seconds
 * @param {number} params.commulative - Cumulative time taken
 * @param {string|number} params.mockTestAttemptID - Mock test attempt ID
 * @param {string|number} params.examId - Exam ID (fallback)
 * @param {Object} params.aiResponse - AI scoring response (optional)
 * @param {string} params.audioUrl - Audio file URL (optional)
 * @param {Array} params.selectedAnswers - Selected answers for multiple choice (optional)
 * @param {Array} params.correctAnswers - Correct answers (optional)
 * @param {Array} params.userOrder - User's order for reorder questions (optional)
 * @param {Array} params.majorAspects - Major aspects for image questions (optional)
 * @param {Array} params.minorAspects - Minor aspects for image questions (optional)
 * @param {Array} params.optionNames - Option names for questions (optional)
 * @returns {FormData} FormData object ready for submission
 */
export function createMockTestPayload(params) {
  const {
    quest,
    questData,
    userResponse,
    marksObtained,
    timeTaken,
    commulative,
    mockTestAttemptID,
    examId,
    aiResponse = {},
    audioUrl = null,
    selectedAnswers = [],
    correctAnswers = [],
    userOrder = [],
    majorAspects = [],
    minorAspects = [],
    optionNames = []
  } = params;

  const formData = new FormData();

  // Core payload fields - ensure proper types
  formData.append("mock_test_question_id", Number(quest.MockTestQuestionTableId));
  formData.append("mock_test_attempt_id", Number(mockTestAttemptID || examId));
  formData.append("marks_obtained", Number(marksObtained));
  formData.append("is_ptecore", false); // boolean
  formData.append("time_taken", Number(timeTaken));

  // Timing information - ensure proper types
  formData.append("all_times.Category", quest.Category);
  if (commulative === null) {
    formData.append("all_times.commulativeTime", Number(0));
  } else {
    formData.append("all_times.commulativeTime", Number(commulative + timeTaken));
  }

  // User response structure based on question type
  const userResponseData = createUserResponseData(quest, questData, userResponse, aiResponse, audioUrl, selectedAnswers, correctAnswers, userOrder, marksObtained, optionNames);
  
  // Append user response fields as dotted keys with proper array handling
  Object.entries(userResponseData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Handle arrays with indexed keys
      value.forEach((item, index) => {
        formData.append(`user_response.${key}[${index}]`, item);
      });
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects with dotted keys
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (Array.isArray(nestedValue)) {
          // Handle nested arrays
          nestedValue.forEach((item, index) => {
            formData.append(`user_response.${key}.${nestedKey}[${index}]`, item);
          });
        } else {
          formData.append(`user_response.${key}.${nestedKey}`, nestedValue);
        }
      });
    } else {
      // Handle simple values
      formData.append(`user_response.${key}`, value);
    }
  });

  // Add enableSkillsData with proper dotted keys for tests that need it
  // Only add for specific test types that require skills data
  if (correctAnswers.length > 0 && shouldAddEnableSkillsData(quest.SubCategory)) {
    let component = "Choice"; // Default component
    
    // Determine component based on test type
    if (quest.SubCategory.includes("Fill in the Blanks")) {
      component = "Blanks";
    } else if (quest.SubCategory.includes("Multiple Choice")) {
      component = "Choice";
    } else if (quest.SubCategory.includes("Highlight") || quest.SubCategory.includes("Select Missing Word")) {
      component = "Blanks";
    }
    
    const totalAnswers = correctAnswers.length;
    const suggestion = generateSuggestion(marksObtained, totalAnswers);
    
    formData.append("user_response.enableSkillsData[0].component", component);
    formData.append("user_response.enableSkillsData[0].score", `${marksObtained}/${totalAnswers}`);
    formData.append("user_response.enableSkillsData[0].suggestion", suggestion);
  }

  // AI scoring payload (only for AI-enabled tests)
  if (isAITest(quest.SubCategory)) {
    const aiScoringPayload = createAIScoringPayload(quest, questData, userResponse, audioUrl, majorAspects, minorAspects, optionNames);
    
    Object.entries(aiScoringPayload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle nested arrays for major_aspect and minor_aspect in Writing tests
        value.forEach((subArray, index) => {
          if (Array.isArray(subArray)) {
            // Handle nested sub-arrays
            subArray.forEach((item, subIndex) => {
              formData.append(`payload_ai_scoring.${key}[${index}][${subIndex}]`, item);
            });
          } else {
            // Handle simple arrays
            formData.append(`payload_ai_scoring.${key}[${index}]`, subArray);
          }
        });
      } else {
        // Handle simple values
        formData.append(`payload_ai_scoring.${key}`, value);
      }
    });
  }

  // Attempt information
  formData.append("attempt_info.test_name", quest.SubCategory);
  formData.append("attempt_info.attempt_mode", "mock");

  return formData;
}

/**
 * Creates user response data based on question type
 */
function createUserResponseData(quest, questData, userResponse, aiResponse, audioUrl, selectedAnswers, correctAnswers, userOrder, marksObtained, optionNames) {
  const baseResponse = {
    QuestionId: quest?.QuestionId || "",
    QuestionStatement: quest?.QuestionStatement ?? 
      (Array.isArray(questData?.AnswerNames) ? questData.AnswerNames.join(" ") : questData?.AnswerNames) ?? "",
    QuestionName: quest?.QuestionName || "",
    Category: quest?.Category || "",
    SubCategory: quest?.SubCategory || ""
  };

  // Add AI response for AI-enabled tests
  if (isAITest(quest.SubCategory)) {
    // Always add AI_response for AI tests, even if it's empty
    baseResponse.AI_response = Object.keys(aiResponse || {}).length === 0 ? "{}" : aiResponse;
    
    // Only add audio_url for tests that actually have audio (Speaking tests)
    if (quest.Category === "Speaking") {
      baseResponse.audio_url = audioUrl;
    }
  }

  // Add question-specific data based on type
  switch (quest.SubCategory) {
    // Speaking tests
    case "Read Aloud":
    case "Repeat Sentence":
    case "Re-tell Lecture":
    case "Summarize Group Discussion":
    case "Respond to a situation":
      baseResponse.UserResponse = userResponse;
      break;

    case "Describe Image":
      baseResponse.UserResponse = userResponse;
      break;

    case "Answer Short Question":
      baseResponse.UserResponse = userResponse;
      break;

    // Writing tests
    case "Write Essay":
    case "Write Email":
    case "Summarize Written Text":
      baseResponse.UserResponse = userResponse;
      break;

    // Reading tests
    case "Reading & Writing: Fill in the Blanks":
    case "Reading: Fill in the Blanks":
      baseResponse.correctAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
      baseResponse.selectedAnswers = Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers];
      // Don't add enableSkillsData here - it will be handled separately with dotted keys
      break;

    case "Multiple Choice, Multiple Answers":
    case "Multiple Choice, Single Answer":
      baseResponse.correctAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
      baseResponse.selectedAnswers = Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers];
      // Don't add enableSkillsData here - it will be handled separately with dotted keys
      break;

    case "Re-order Paragraphs":
      baseResponse.submissionResult = {
        userIndexes: userOrder,
        correctIndexes: correctAnswers,
        score: marksObtained
      };
      break;

    // Listening tests
    case "Summarize Spoken Text":
    case "Write from Dictation":
      baseResponse.UserResponse = userResponse;
      break;

    case "Listening: Multiple Choice, Multiple Answers":
    case "Listening: Multiple Choice, Single Answer":
      baseResponse.correctAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
      baseResponse.selectedAnswers = Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers];
      // Don't add enableSkillsData here - it will be handled separately with dotted keys
      break;

    case "Fill in the Blanks":
      baseResponse.correctAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
      baseResponse.selectedAnswers = Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers];
      // Don't add enableSkillsData here - it will be handled separately with dotted keys
      break;

    case "Highlight Correct Summary":
    case "Select Missing Word":
      baseResponse.correctAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
      baseResponse.selectedAnswers = Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers];
      // Don't add enableSkillsData here - it will be handled separately with dotted keys
      break;

    case "Highlight Incorrect Words":
      baseResponse.correctAnswers = Array.isArray(correctAnswers) ? correctAnswers : [correctAnswers];
      baseResponse.selectedAnswers = Array.isArray(selectedAnswers) ? selectedAnswers : [selectedAnswers];
      // Don't add enableSkillsData here - it will be handled separately with dotted keys
      break;

    default:
      // Fallback for unknown question types
      baseResponse.UserResponse = userResponse;
      break;
  }

  return baseResponse;
}

/**
 * Creates AI scoring payload for AI-enabled tests
 */
function createAIScoringPayload(quest, questData, userResponse, audioUrl, majorAspects, minorAspects, optionNames) {
  // Handle undefined questData
  if (!questData) {
    console.warn("questData is undefined in createAIScoringPayload");
    return {};
  }

  switch (quest.SubCategory) {
    // Speaking tests
    case "Read Aloud":
      return {
        audio_url: audioUrl,
        script: questData.QuestionStatement || "",
        user_response: userResponse
      };

    case "Repeat Sentence":
      return {
        audio_url: audioUrl,
        correct_text: questData.QuestionStatement || "",
        user_text: userResponse
      };

    case "Describe Image":
      return {
        audio_url: audioUrl,
        user_text: userResponse,
        major_aspects: JSON.stringify((majorAspects || []).flat()),
        minor_aspects: JSON.stringify((minorAspects || []).flat())
      };

    case "Re-tell Lecture":
      return {
        audio_url: audioUrl,
        script: questData.QuestionStatement || "",
        user_response: userResponse
      };

    case "Summarize Group Discussion":
      return {
        audio_url: audioUrl,
        script: questData.QuestionStatement || "",
        user_response: userResponse
      };

    case "Respond to a situation":
      return {
        audio_url: audioUrl,
        user_text: userResponse,
        major_aspects: JSON.stringify((majorAspects || []).flat()),
        minor_aspects: JSON.stringify((minorAspects || []).flat())
      };

    case "Answer Short Question":
      return {
        audio_url: audioUrl,
        question: questData.QuestionStatement || "",
        user_answer: userResponse,
        answer_list: optionNames || []
      };

    // Writing tests
    case "Write Essay":
      return {
        essay: userResponse,
        // major_aspect: majorAspects, // Send as nested array: [["fake news"]]
        // minor_aspect: minorAspects, // Send as nested array: [["social network", "social networks"], ["strictly"]]
        major_aspect: (majorAspects || []).flat(), // Flatten to single array: ["fake news"]
        minor_aspect: (minorAspects || []).flat(), // Flatten to single array: ["social network", "social networks", "strictly"]
        question: questData.QuestionStatement || ""
      };

    case "Write Email":
      return {
        desc: questData.QuestionStatement || "",
        email: userResponse,
        // major_aspect: majorAspects, // Send as nested array: [["fake news"]]
        // minor_aspect: minorAspects  // Send as nested array: [["social network", "social networks"], ["strictly"]]
        major_aspect: (majorAspects || []).flat(), // Flatten to single array: ["fake news"]
        minor_aspect: (minorAspects || []).flat()  // Flatten to single array: ["social network", "social networks", "strictly"]
      };

    case "Summarize Written Text":
      return {
        passage: questData.QuestionStatement || "",
        pte_type: "pte academic",
        summary: userResponse
      };

    // Listening tests
    case "Summarize Spoken Text":
      return {
        passage: questData.QuestionStatement || "",
        summary: userResponse,
        pte_type: "pte academic"
      };

    case "Write from Dictation":
      return {
        correct_answer: typeof questData.AnswerNames === "string" 
          ? questData.AnswerNames 
          : (questData.AnswerNames || []).join(" "),
        user_response: userResponse
      };

    default:
      return {};
  }
}

/**
 * Checks if a test type requires AI scoring
 */
function isAITest(subCategory) {
  if (!subCategory) return false;
  const normalized = String(subCategory).toLowerCase();
  const testConfig = TESTS_CONFIG.find(
    (test) => String(test.test_name).toLowerCase() === normalized
  );
  return testConfig && testConfig.processing_mode === "ai";
}

/**
 * Checks if a test type should include enableSkillsData
 */
function shouldAddEnableSkillsData(subCategory) {
  const testsWithSkillsData = [
    "Reading & Writing: Fill in the Blanks",
    "Multiple Choice, Multiple Answers", 
    "Multiple Choice, Single Answer",
    "Reading: Fill in the Blanks",

    "Listening: Multiple Choice, Multiple Answers",
    "Listening: Multiple Choice, Single Answer",
    "Fill in the Blanks",
    "Highlight Incorrect Words",
    "Highlight Correct Summary",
    "Select Missing Word"
  ];
  
  return testsWithSkillsData.includes(subCategory);
}

/**
 * Generates a suggestion based on marks obtained and total answers.
 * @param {number} marksObtained - The score obtained by the user.
 * @param {number} totalAnswers - The total number of answers for the question.
 * @returns {string} A suggestion based on the score.
 */
function generateSuggestion(marksObtained, totalAnswers) {
  if (marksObtained === 0) {
    return "You need to improve your skills. Keep practicing!";
  } else if (marksObtained === totalAnswers) {
    return "Excellent! You have mastered this skill.";
  } else if (marksObtained > totalAnswers * 0.7) {
    return "Good job! You are on the right track.";
  } else if (marksObtained > totalAnswers * 0.5) {
    return "You are doing well. Keep practicing.";
  } else {
    return "You need to work on this skill. Practice more.";
  }
}

/**
 * Helper function to create payload for forced submission (time up scenarios)
 */
export function createForcedSubmissionPayload(quest, mockTestAttemptID, examId, commulative, questionsTime) {
  return createMockTestPayload({
    quest,
    questData: {},
    userResponse: "",
    marksObtained: -100,
    timeTaken: 0,
    commulative: commulative + questionsTime / 1000,
    mockTestAttemptID,
    examId,
    aiResponse: {},
    audioUrl: null,
    selectedAnswers: [],
    correctAnswers: [],
    userOrder: [],
    majorAspects: [],
    minorAspects: [],
    optionNames: []
  });
}
